import { useEffect, useImperativeHandle, useState } from "react";
import braintree, { HostedFields, HostedFieldsEvent } from "braintree-web";
import { Stack } from "@/components/ui/Stack";
import { FormControl } from "@/components/ui/FormControl";
import { Spinner } from "@/components/ui/Spinner";
import classes from "./CardDetails.module.scss";
import { clsx } from "clsx";
import { CardDetailsProps } from "./CardDetailsProps";
import { useBraintree } from "@/hooks/useBraintree";
import { trackError } from "@/utils/error-monitoring";
import { useCardDetailsValidation } from "./useCardDetailsValidation";
import { useCardDetailsErrorsStore } from "./useCardDetailsErrorsStore";
import { useTranslation } from "@/hooks/useTranslation";

export function CardDetails(props: CardDetailsProps) {
  const { className, ref } = props;

  const { validate, validateField, validFields } = useCardDetailsValidation();
  const isValidated = useCardDetailsErrorsStore((state) => state.isValidated);
  const errors = useCardDetailsErrorsStore((state) => state.errors);

  const { braintreeClient } = useBraintree();
  const [braintreeInstance, setBraintreeInstance] = useState<HostedFields>();
  const [loading, setLoading] = useState(true);
  const [focusedField, setFocusedField] = useState<string | null>(null);

  const t = useTranslation("card_details");

  useImperativeHandle(ref, () => ({
    validate: async () => {
      const errors = await validate(braintreeInstance);

      if (!errors) {
        return {
          isValid: true,
        };
      }

      if (errors.number) {
        braintreeInstance?.focus("number");
      } else if (errors.expirationDate) {
        braintreeInstance?.focus("expirationDate");
      } else if (errors.cvv) {
        braintreeInstance?.focus("cvv");
      }

      return {
        isValid: false,
      };
    },
  }));

  useEffect(() => {
    let instance: HostedFields;

    const initializeBraintree = async () => {
      if (!braintreeClient) return;

      instance = await braintree.hostedFields.create({
        client: braintreeClient,
        styles: {
          "::placeholder": {
            color: "rgba(0, 0, 0, 0.3)",
          },
          input: {
            "font-size": "1rem",
            "font-family": "system-ui, sans-serif",
            color: "#333333",
          },
        },
        fields: {
          number: {
            selector: "#card-number",
            placeholder: "0000 0000 0000 0000",
          },
          cvv: {
            selector: "#cvv",
            placeholder: "000",
          },
          expirationDate: {
            selector: "#expiration-date",
            placeholder: "MM/YY",
          },
        },
      });

      setLoading(false);
      setBraintreeInstance(instance);

      instance.on("blur", () => {
        setFocusedField(null);
      });

      instance.on("focus", (event: HostedFieldsEvent) => {
        setFocusedField(event.emittedBy);
      });

      instance.on("validityChange", (event: HostedFieldsEvent) => {
        validateField(event);
      });
    };

    try {
      initializeBraintree();
    } catch (error) {
      trackError(error as Error);
    }

    return () => {
      if (instance) {
        instance.teardown().catch((error) => trackError(error as Error));
      }
    };
  }, [braintreeClient, validateField]);

  const errorNumber = isValidated ? errors.number : null;
  const errorExpirationDate = isValidated ? errors.expirationDate : null;
  const errorCvv = isValidated ? errors.cvv : null;

  return (
    <div
      className={clsx(classes.wrapper, className)}
      aria-live="polite"
      aria-busy={loading}
    >
      {loading && (
        <div className={classes.loader}>
          <Spinner />
        </div>
      )}

      <Stack gap="md">
        <FormControl
          htmlFor="card-number"
          label={t("card-number")}
          error={errorNumber}
          isValid={validFields.number}
        >
          <div
            id="card-number"
            className={clsx({
              [classes.input]: true,
              [classes.withIcon]: true,
              [classes.ccNumber]: true,
              [classes.error]: errorNumber,
              [classes.focused]: focusedField === "number",
            })}
          />
        </FormControl>

        <Stack direction="row" gap="lg">
          <FormControl
            htmlFor="expiration-date"
            label={t("expiration-date")}
            error={errorExpirationDate}
            isValid={validFields.expirationDate}
          >
            <div
              id="expiration-date"
              className={clsx({
                [classes.input]: true,
                [classes.withIcon]: true,
                [classes.date]: true,
                [classes.error]: errorExpirationDate,
                [classes.focused]: focusedField === "expirationDate",
              })}
            />
          </FormControl>

          <FormControl
            htmlFor="cvv"
            label={t("cvv")}
            error={errorCvv}
            isValid={validFields.cvv}
          >
            <div
              id="cvv"
              className={clsx({
                [classes.input]: true,
                [classes.withIcon]: true,
                [classes.cvv]: true,
                [classes.error]: errorCvv,
                [classes.focused]: focusedField === "cvv",
              })}
            />
          </FormControl>
        </Stack>
      </Stack>
    </div>
  );
}
